<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  type GroupedReaction,
  getAvatarColor,
  getInitials,
  normalizeAvatarUrl,
} from '../../chatModules'
import { CHAT_FLOATING_Z_INDEX } from '@/modules/chat-matrix-fast/constants/ui'

const MAX_VISIBLE_AVATARS = 5

interface Props {
  reactions: GroupedReaction[]
  messageId?: string | number
}

interface Emits {
  (e: 'reaction-click', reactionTypeId: string | number, isMyReaction: boolean): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const displayGroups = ref<GroupedReaction[]>([])
const failedAvatarKeys = ref<Record<string, boolean>>({})

watch(
  () => props.reactions,
  next => {
    displayGroups.value = next || []
  },
  { deep: true, immediate: true },
)

const reactionsToShow = computed(() => displayGroups.value)
const hasReactions = computed(() => reactionsToShow.value.length > 0)

const getReactionLabel = (group: GroupedReaction): string => {
  if (group.isImage) {
    return group.name || 'Reaction'
  }

  return group.emoji || group.name || 'Reaction'
}

const getGroupKey = (group: GroupedReaction, index: number): string => {
  return `reaction-${props.messageId ?? 'msg'}-${group.reactionTypeId}-${index}`
}

const getVisibleUsers = (group: GroupedReaction) => group.users.slice(0, MAX_VISIBLE_AVATARS)

const getHiddenCount = (group: GroupedReaction) => Math.max(0, group.users.length - MAX_VISIBLE_AVATARS)

const getUserAvatarKey = (group: GroupedReaction, userId: string, prefix = 'pill') =>
  `${prefix}-${props.messageId ?? 'msg'}-${group.reactionTypeId}-${userId}`

const canShowUserAvatar = (
  group: GroupedReaction,
  user: GroupedReaction['users'][number],
  prefix = 'pill',
) => {
  const avatarSrc = normalizeAvatarUrl(user.avatar)
  return Boolean(avatarSrc) && !failedAvatarKeys.value[getUserAvatarKey(group, user.id, prefix)]
}

const markAvatarFailed = (key: string) => {
  if (!failedAvatarKeys.value[key]) {
    failedAvatarKeys.value = {
      ...failedAvatarKeys.value,
      [key]: true,
    }
  }
}
</script>

<template>
  <div
    v-show="hasReactions"
    class="reactions-bar"
  >
    <VMenu
      v-for="(group, index) in reactionsToShow"
      :key="getGroupKey(group, index)"
      open-on-hover
      :close-on-content-click="false"
      location="top"
      offset="8"
      :open-delay="200"
      :close-delay="100"
      :z-index="CHAT_FLOATING_Z_INDEX + 2"
      content-class="reaction-users-menu"
    >
      <template #activator="{ props: menuProps }">
        <button
          type="button"
          class="reaction-pill"
          :class="{ 'reaction-pill--mine': group.isMyReaction }"
          :aria-label="`Реакция ${getReactionLabel(group)}`"
          v-bind="menuProps"
          @click.stop="emit('reaction-click', group.reactionTypeId, group.isMyReaction)"
        >
          <span class="reaction-icon-wrapper">
            <template v-if="group.isImage">
              <img
                :src="group.icon"
                :alt="group.name"
                class="reaction-icon-small"
              >
            </template>
            <span
              v-else
              class="reaction-emoji"
            >{{ group.emoji }}</span>
          </span>
          <span
            v-if="group.users.length"
            class="reaction-avatars"
          >
            <span
              v-for="user in getVisibleUsers(group)"
              :key="user.id"
              class="reaction-user-badge"
              :class="[!canShowUserAvatar(group, user) ? getAvatarColor(user.name) : '']"
            >
              <img
                v-if="canShowUserAvatar(group, user)"
                :src="normalizeAvatarUrl(user.avatar)!"
                alt=""
                @error="markAvatarFailed(getUserAvatarKey(group, user.id))"
              >
              <span
                v-else
                class="reaction-user-initials"
              >
                {{ getInitials(user.name) }}
              </span>
            </span>
            <span
              v-if="getHiddenCount(group) > 0"
              class="reaction-user-badge reaction-user-badge--more"
            >
              +{{ getHiddenCount(group) }}
            </span>
          </span>
        </button>
      </template>

      <div class="reaction-users-popover">
        <div
          v-for="user in group.users"
          :key="user.id"
          class="reaction-users-popover__item"
        >
          <span
            class="reaction-users-popover__avatar"
            :class="[!canShowUserAvatar(group, user, 'menu') ? getAvatarColor(user.name) : '']"
          >
            <img
              v-if="canShowUserAvatar(group, user, 'menu')"
              :src="normalizeAvatarUrl(user.avatar)!"
              alt=""
              @error="markAvatarFailed(getUserAvatarKey(group, user.id, 'menu'))"
            >
            <span
              v-else
              class="reaction-users-popover__initials"
            >
              {{ getInitials(user.name) }}
            </span>
          </span>
          <span class="reaction-users-popover__name">{{ user.name }}</span>
        </div>
      </div>
    </VMenu>
  </div>
</template>

<style scoped>
.reactions-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
}

.reaction-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: auto;
  padding: 2px 4px 2px 6px;
  border-radius: 9999px;
  transform: none !important;
}

.reaction-pill:hover {
  transform: none !important;
}

.reaction-pill--mine {
  background: rgba(var(--v-theme-primary), 0.18);
}

.reaction-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  line-height: 1;
}

.reaction-emoji {
  display: block;
  width: 18px;
  height: 18px;
  font-size: 13px;
  line-height: 18px;
  text-align: center;
}

.reaction-icon-small {
  width: 14px;
  height: 14px;
  object-fit: contain;
  display: block;
}

.reaction-avatars {
  display: inline-flex;
  align-items: center;
}

.reaction-user-badge {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  pointer-events: none;
}

.reaction-user-badge + .reaction-user-badge {
  margin-left: -6px;
}

.reaction-user-badge img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.reaction-user-initials {
  font-size: 8px;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 0 3px rgba(0, 0, 0, 0.45);
  line-height: 1;
  letter-spacing: 0.1px;
}

.reaction-user-badge--more {
  font-size: 8px;
  font-weight: 700;
  color: rgba(var(--v-theme-on-surface), 0.75);
  background: rgba(var(--v-theme-surface), 0.95);
}
</style>

<style>
.reaction-users-menu {
  box-shadow: none !important;
  background: transparent !important;
  overflow: visible !important;
}

.reaction-users-popover {
  min-width: 180px;
  max-width: 280px;
  max-height: 240px;
  overflow-y: auto;
  padding: 6px;
  background: rgb(var(--v-theme-surface));
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
}

.reaction-users-popover__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
}

.reaction-users-popover__item:hover {
  background: rgba(var(--v-theme-on-surface), 0.06);
}

.reaction-users-popover__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.reaction-users-popover__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.reaction-users-popover__initials {
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  line-height: 1;
}

.reaction-users-popover__name {
  font-size: 14px;
  line-height: 1.3;
  color: rgba(var(--v-theme-on-surface), 0.9);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
